<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    scheduledMessagesCount: {
      type: Number,
      default: 0,
    },
  },
  emits: [
    'setReplyMode',
    'toggleEditorSize',
    'executeCopilotAction',
    'scheduleMessage',
  ],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleSelectMode = mode => {
      if (mode === REPLY_EDITOR_MODES.REPLY) {
        handleReplyClick();
      } else {
        handleNoteClick();
      }
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleSelectMode,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
    hasScheduledMessages() {
      return this.scheduledMessagesCount > 0;
    },
    scheduleButtonTooltip() {
      return this.hasScheduledMessages
        ? this.$t('CONVERSATION.SCHEDULE_MESSAGE.BUTTON_TOOLTIP_WITH_COUNT', {
            count: this.scheduledMessagesCount,
          })
        : this.$t('CONVERSATION.SCHEDULE_MESSAGE.BUTTON_TOOLTIP');
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-1 md:gap-2 h-11 md:h-[3.25rem] items-center ltr:pl-2 ltr:pr-1 rtl:pr-2 rtl:pl-1 md:ltr:pl-3 md:ltr:pr-2 md:rtl:pr-3 md:rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @select-mode="handleSelectMode"
    />
    <!-- Only rendered when there's a warning to show. It used to be an always
         present wrapper carrying mx-4, silently eating 32px of the row even
         when empty — on a phone that was the difference between the mode
         toggle fitting on one line and wrapping onto two. -->
    <div
      v-if="isMessageLengthReachingThreshold"
      class="flex items-center min-w-0 mx-2 md:mx-4 my-0 text-xs"
    >
      <span :class="charLengthClass" class="truncate">
        {{ characterLengthWarning }}
      </span>
    </div>
    <div class="flex items-center gap-0.5 md:gap-2 flex-shrink-0">
      <NextButton
        v-tooltip.top="scheduleButtonTooltip"
        :variant="hasScheduledMessages ? 'faded' : 'ghost'"
        :color="hasScheduledMessages ? 'blue' : 'slate'"
        :disabled="disabled || isEditorDisabled"
        size="md"
        class="!size-8 md:!size-10"
        @click="$emit('scheduleMessage')"
      >
        <template #icon>
          <span class="i-ph-clock-countdown size-4 md:size-5" />
        </template>
      </NextButton>
      <template v-if="captainTasksEnabled">
        <div class="relative">
          <NextButton
            ref="copilotToggleRef"
            ghost
            :disabled="disabled || isEditorDisabled"
            :class="{
              'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
              'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
            }"
            size="md"
            class="!size-8 md:!size-10"
            @click="toggleCopilotMenu"
          >
            <template #icon>
              <span class="i-ph-sparkle-fill size-4 md:size-5" />
            </template>
          </NextButton>
          <CopilotMenuBar
            v-if="showCopilotMenu"
            v-on-click-outside="[
              handleClickOutside,
              { ignore: [copilotToggleRef] },
            ]"
            class="ltr:right-0 rtl:left-0 bottom-full mb-2"
            @execute-copilot-action="handleCopilotAction"
          />
        </div>
        <NextButton
          ghost
          class="text-n-slate-11 !size-8 md:!size-10"
          size="md"
          @click="$emit('toggleEditorSize')"
        >
          <template #icon>
            <span class="i-lucide-maximize-2 size-4 md:size-5" />
          </template>
        </NextButton>
      </template>
    </div>
  </div>
</template>
